import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type WorkflowRun, listWorkflowRuns } from "../api/workflows.ts";

const STATUS_COLORS: Record<string, string> = {
  RUNNING: "text-blue-400 bg-blue-900/30",
  COMPLETED: "text-green-400 bg-green-900/30",
  FAILED: "text-red-400 bg-red-900/30",
  CANCELLED: "text-gray-400 bg-gray-800",
  WAITING_APPROVAL: "text-yellow-400 bg-yellow-900/30",
};

export function WorkflowsPage() {
  const { token } = useAuth();
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listWorkflowRuns(token, { limit: 50 })
      .then((res) => setRuns(res))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load runs"))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Workflow Runs</h1>

        {loading && <p className="text-gray-400">Loading...</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && runs.length === 0 && <p className="text-gray-500">No workflow runs yet.</p>}

        <div className="space-y-2">
          {runs.map((run) => (
            <div
              key={run.id}
              className="space-y-2 rounded-lg border border-gray-800 bg-gray-900 p-4"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="font-mono text-sm text-white">{run.workflowId}</span>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[run.status] ?? "bg-gray-800 text-gray-400"}`}
                >
                  {run.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                {run.currentStep != null && <span>step: {run.currentStep}</span>}
                <span>correlation: {run.correlationId.slice(0, 8)}…</span>
                <span>started: {new Date(run.startedAt).toLocaleString()}</span>
                {run.completedAt != null && (
                  <span>completed: {new Date(run.completedAt).toLocaleString()}</span>
                )}
              </div>
              {run.errorMessage != null && (
                <p className="font-mono text-xs text-red-400">{run.errorMessage}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
